import { useToast } from '@chakra-ui/toast';
import axios, { AxiosError } from 'axios';
import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import wbsBookingsApi from '../services/apis/wbsBookingsApi';

import {
  authenticateSessionsService,
  ISessionDetailedFeatureGroup,
  INewSession,
  ISessionUser,
} from '../services/Auth/AuthenticateSessionsService';
import { socket } from '../socket';
import { translateError } from '../utils/errors';

// interface IRefreshingQueueItem {
//   onSuccess: (token: string) => void;
//   onFailure: (error: AxiosError) => void;
// }

interface IAuthUser extends ISessionUser {
  featureGroup: ISessionDetailedFeatureGroup;
}

interface IAuthState extends INewSession {
  user: IAuthUser;
}

interface ISignInCredentials {
  email: string;
  password: string;
}

interface IAuthContextData {
  user: IAuthUser;
  signIn(credentials: ISignInCredentials): Promise<void>;
  signOut: () => void;
  loading: boolean;
  updateUser: (user: IAuthUser) => void;
}

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IAuthState>(() => {
    const refreshToken = localStorage.getItem('@WBS-BOOKINGS:refreshToken');
    const token = localStorage.getItem('@WBS-BOOKINGS:token');
    const user = localStorage.getItem('@WBS-BOOKINGS:user');
    if (refreshToken && token && user) {
      wbsBookingsApi.defaults.headers.authorization = `Bearer ${token}`;

      return { refreshToken, token, user: JSON.parse(user) };
    }

    return {} as IAuthState;
  });

  useEffect(() => {
    if (data.user) {
      socket.emit('connected-users', {
        userId: data.user.id,
        featureGroup: data.user.featureGroup.key,
      });
    }
  }, [data.user]);

  const signIn = useCallback(
    async ({ email, password }) => {
      setLoading(true);
      try {
        const sessionData = await authenticateSessionsService({
          email,
          password,
        });

        const { refreshToken, token, user } = sessionData;

        localStorage.setItem('@WBS-BOOKINGS:refreshToken', refreshToken);
        localStorage.setItem('@WBS-BOOKINGS:token', token);
        localStorage.setItem('@WBS-BOOKINGS:user', JSON.stringify(user));

        wbsBookingsApi.defaults.headers.authorization = `Bearer ${token}`;

        setData({ refreshToken, token, user: user as IAuthUser });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha na autenticação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao fazer o login, verifique os dados e tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem('@WBS-BOOKINGS:refreshToken');
    localStorage.removeItem('@WBS-BOOKINGS:token');
    localStorage.removeItem('@WBS-BOOKINGS:user');

    setData({} as IAuthState);
  }, []);

  const updateUser = useCallback((user: IAuthUser) => {
    localStorage.setItem('@WBS-BOOKINGS:user', JSON.stringify(user));

    setData((prev) => ({
      refreshToken: prev.refreshToken,
      token: prev.token,
      user,
    }));
  }, []);

  useEffect(() => {
    // let failedRequestQueue: Array<IRefreshingQueueItem> = [];
    // let isRefreshing = false;

    wbsBookingsApi.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // if (error.response?.data.message === 'jwt expired') {
          //   const originalConfig = error.config;
          //   const refreshToken = localStorage.getItem(
          //     '@WBS-BOOKINGS:refreshToken',
          //   );

          //   if (!isRefreshing && refreshToken) {
          //     isRefreshing = true;

          //     try {
          //       refreshTokensService(refreshToken).then(
          //         ({ refreshToken: newRefreshToken, token: newToken }) => {
          //           localStorage.setItem(
          //             '@WBS-BOOKINGS:refreshToken',
          //             newRefreshToken,
          //           );

          //           localStorage.setItem('@WBS-BOOKINGS:token', newToken);

          //           wbsBookingsApi.defaults.headers.authorization = `Bearer ${newToken}`;

          //           failedRequestQueue.forEach((request) =>
          //             request.onSuccess(newToken),
          //           );

          //           failedRequestQueue = [];

          //           setData((prevState) => ({
          //             ...prevState,
          //             token: newToken,
          //             refreshToken: newRefreshToken,
          //           }));
          //         },
          //       );
          //       // eslint-disable-next-line @typescript-eslint/no-explicit-any
          //     } catch (err: any) {
          //       failedRequestQueue.forEach((request) => request.onFailure(err));

          //       failedRequestQueue = [];
          //     } finally {
          //       isRefreshing = false;
          //     }
          //   }

          //   return new Promise((resolve, reject) => {
          //     failedRequestQueue.push({
          //       onSuccess: (newToken: string) => {
          //         originalConfig.headers.authorization = `Bearer ${newToken}`;

          //         resolve(wbsBookingsApi(originalConfig));
          //       },
          //       onFailure: (err: AxiosError) => {
          //         reject(err);
          //       },
          //     });
          //   });
          // }

          toast({
            title: 'Sessão expirada',
            description: 'Por favor, faça login novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });

          signOut();
        }

        return Promise.reject(error);
      },
    );
  }, [signOut, toast]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        loading,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): IAuthContextData => useContext(AuthContext);

export { AuthProvider, useAuth };
